import React, { useState, useEffect, useMemo } from "react";
import { executeWorkflowHandshake } from "../Workflow_XLS_Support";

export default function TemplateBuilderMainView() {
  const [inventory, setInventory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- 🕵️ AUDIT CONTROLLER STATE (Mirrored from Workflow_Validation_View) ---
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    const fetchInventory = async () => {
      setLoading(true);
      try {
        const payload = {
          action: "GET_INVENTORY",
          user_id: sessionStorage.getItem("SEC_USER_GUID") || "cdbd2330-c2ef-46d4-8dc9-c34471b0475d",
          payload: { bucket: "esg-staging-clean", prefix: "" },
          siteUserGuid: sessionStorage.getItem("SEC_USER_GUID") || "cdbd2330-c2ef-46d4-8dc9-c34471b0475d",
          sessionID: sessionStorage.getItem("SEC_SESSION_ID") || "b3bae30f-7f81-4437-88a8-4178ca232815"
        };

        const response = await executeWorkflowHandshake("doorman/inventory", payload, "Inventory");
        
        // 🎯 Extraction: Matches "return {'files': hydrated_collection}" from Inventory Service
        const container = response?.Data || response?.data || response;
        const fileList = container?.files || [];
        
        setInventory(fileList);
      } catch (err) {
        console.error("🚨 [FETCH_ERROR]:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInventory();
  }, []);

  // --- 🕵️ AUDIT RESULTS LOGIC (Mirrored from Workflow_Validation_View) ---
  const auditResults = useMemo(() => {
    if (!Array.isArray(inventory)) return null;

    let filtered = inventory;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        (f.filename?.toLowerCase().includes(q)) ||
        (f.path?.toLowerCase().includes(q))
      );
    }

    const totalRows = filtered.length;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedRows = filtered.slice(startIndex, startIndex + rowsPerPage);

    return {
      paginatedRows,
      totalRows,
      totalPages: Math.ceil(totalRows / rowsPerPage) || 1
    };
  }, [inventory, searchQuery, currentPage]);

  // --- UI LAYOUT (Standard Nesting to clear Nav Bar) ---
  const LayoutWrapper = ({ children, title, subtitle }) => (
    <main className="page-main mt-5 pt-5" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section px-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-8">
                <div className="d-flex justify-content-between align-items-end mb-4 border-bottom pb-3">
                  <div>
                    <h2 className="h4 fw-bold mb-1 text-dark">{title}</h2>
                    <div className="text-muted extra-small text-uppercase fw-bold">{subtitle}</div>
                  </div>
                  {selectedFile && (
                    <button className="btn btn-xs btn-outline-danger fw-bold" onClick={() => setSelectedFile(null)}>
                      CHANGE TAXONOMY
                    </button>
                  )}
                </div>
                {children}
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );

  if (!selectedFile) {
    return (
      <LayoutWrapper title="Template Builder" subtitle="Clean Room Inventory Selection">
        <div className="card shadow-sm border-0 p-4 bg-white animate__animated animate__fadeIn">
          
          {/* 1. SEARCH (Mirrored from Validation View style) */}
          <div className="mb-4">
            <label className="extra-small fw-bold text-muted text-uppercase mb-2">1. Search Inventory</label>
            <input 
              type="text" 
              className="form-control shadow-none" 
              placeholder="Deep search filenames..."
              value={searchQuery} 
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>

          {/* 2. LIST DISPLAY */}
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : (
            <div className="list-group list-group-flush border rounded mb-4">
              {auditResults?.paginatedRows.length > 0 ? auditResults.paginatedRows.map((file, i) => (
                <button key={i} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                  onClick={() => setSelectedFile(file)}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-file-earmark-code text-primary me-3 fs-5"></i>
                    <span className="fw-bold text-dark">{file.filename}</span>
                  </div>
                  <i className="bi bi-chevron-right text-muted small"></i>
                </button>
              )) : (
                <div className="p-5 text-center text-muted small italic">No validated taxonomies found.</div>
              )}
            </div>
          )}

          {/* 3. PAGINATION (Mirrored from Validation View button-group) */}
          {!loading && auditResults?.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center px-1">
              <span className="extra-small text-muted italic">Showing {auditResults.paginatedRows.length} of {auditResults.totalRows}</span>
              <div className="btn-group shadow-sm">
                <button className="btn btn-xs btn-outline-dark fw-bold" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>START</button>
                <button className="btn btn-sm btn-outline-dark fw-bold" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>PREV</button>
                <button className="btn btn-sm btn-primary px-3 disabled">{currentPage}</button>
                <button className="btn btn-sm btn-outline-dark fw-bold" disabled={currentPage === auditResults.totalPages} onClick={() => setCurrentPage(p => p + 1)}>NEXT</button>
                <button className="btn btn-xs btn-outline-dark fw-bold" disabled={currentPage === auditResults.totalPages} onClick={() => setCurrentPage(auditResults.totalPages)}>FINISH</button>
              </div>
            </div>
          )}
        </div>
      </LayoutWrapper>
    );
  }

  // --- VIEW 2: WORKSPACE ---
  return (
    <LayoutWrapper title="Template Workspace" subtitle={`Active: ${selectedFile.filename}`}>
      <div className="card shadow-sm border-0 p-5 bg-white text-center">
         <div className="py-5">
            <div className="spinner-grow text-primary mb-3"></div>
            <h5 className="text-muted italic">[Builder Engine Initializing for {selectedFile.filename}...]</h5>
         </div>
      </div>
    </LayoutWrapper>
  );
}